import { action, makeObservable, observable } from 'mobx';
import { gameModuleService } from '../services/gameModuleService';
import { GAME_SPEED_TYPES } from '../../constants/gameConstants';
import { gameGlobalStates } from '../../constants/reelConstants';
import { specialIconKeys } from '../../constants/symbolsNames';

class GameModuleStore {
  initialMatrix = [];
  resultMatrix = [];
  doSpin = null;
  soundService = null;
  animationEnd = null;
  gameGlobalState = gameGlobalStates.none;
  spinSpeedType = 'standard';
  spinDirection = 1;
  additional = null;
  isForceStop = false;
  awaitForceStop = false;
  canTouchControl = true;
  winAnimations = [];
  responseReady = false;
  scatter1Count = 0;
  scatter2Count = 0;
  isIntro = false;
  wildIcons = [];

  constructor() {
    makeObservable(this, {
      initialMatrix: observable,
      resultMatrix: observable,
      isIntro: observable,
      additional: observable,
      doSpin: observable,
      soundService: observable,
      animationEnd: observable,
      gameGlobalState: observable,
      spinSpeedType: observable,
      spinDirection: observable,
      isForceStop: observable,
      awaitForceStop: observable,
      canTouchControl: observable,
      winAnimations: observable,
      responseReady: observable,
      scatter1Count: observable,
      scatter2Count: observable,
      getPropsFromFrame: action.bound,
      setResultMatrix: action.bound,
      setSpinSpeedType: action.bound,
      setSpinDirection: action.bound,
      handleReelAnimationEnd: action.bound,
      finishRoundWithoutWin: action.bound,
      setResponseReady: action.bound,
      setAdditional: action.bound,
      setIsForceStop: action.bound,
      setGameGlobalState: action.bound,
      setCanTouchControl: action.bound,
      setWinAnimations: action.bound,
      setAwaitForceStop: action.bound,
      setScatterCount: action.bound,
      setWildIcons: action.bound,
    });
  }

  // RU: `isIntro` приходит только детям интро (introChildrenProps); у этой игры интро нет, и
  //     проп остаётся undefined — поле нужно общим хелперам (свайп, терминал показа линий).
  getPropsFromFrame = ({ initialMatrix, doSpin, soundService, animationEnd, isIntro }) => {
    this.initialMatrix = initialMatrix;
    this.doSpin = doSpin;
    this.soundService = soundService;
    this.animationEnd = animationEnd;
    this.isIntro = Boolean(isIntro);
  };

  setSpinSpeedType = (type) => {
    this.spinSpeedType = type;
  };

  setGameGlobalState = (state) => {
    this.gameGlobalState = state;
  };

  setSpinDirection = (direction) => {
    this.spinDirection = direction;
  };

  // RU: пустой `wins` — это проигрыш: `[]` истинно, и прежде доска уходила в показ выигрыша без
  //     единой линии. Раскрытие wild без выигрыша закрывается тоже как проигрыш (в сервисе).
  handleReelAnimationEnd = () => {
    const { wins, expandingWild } = this.additional || {};
    const hasWins = Boolean(wins?.length);
    this.setGameGlobalState(hasWins ? gameGlobalStates.show : gameGlobalStates.none);
    if (expandingWild?.includes(specialIconKeys.wild)) {
      gameModuleService.startWildAnimation(expandingWild, hasWins ? wins : []);
    } else if (hasWins) {
      gameModuleService.startWinAnimation(wins);
    } else {
      this.finishRoundWithoutWin();
    }
    this.setAwaitForceStop(false);
    this.setIsForceStop(false);
    this.setResponseReady(false);
  };

  // RU: свайп поднимается ДО отчёта ядру: на `noWin` ядро может сразу запустить следующий спин
  //     (автоигра), и его `startSpin` должен опустить флаг последним.
  finishRoundWithoutWin = () => {
    this.setCanTouchControl(true);
    this.animationEnd('noWin');
  };

  setResponseReady = (status) => {
    this.responseReady = status;
  };

  setAdditional = (additional) => {
    this.additional = additional;
  };

  // RU: гард — только на ВЗВОД. В турбо барабаны и так идут мимо, а повторный взвод не нужен:
  //     барабаны реагируют на смену значения. Прежде гард стоял и на сброс — и
  //     `setIsForceStop(false)` при поднятом флаге не делал ничего.
  setIsForceStop = (status) => {
    if (status && (this.spinSpeedType === GAME_SPEED_TYPES.turbo || this.isForceStop)) return;
    this.isForceStop = status;
  };

  setAwaitForceStop = (status) => {
    this.awaitForceStop = status;
  };

  setCanTouchControl = (status) => {
    this.canTouchControl = status;
  };

  setWinAnimations = (animation) => {
    if (animation) this.winAnimations.push(animation);
    else this.winAnimations = [];
  };

  setResultMatrix = (matrix) => {
    this.resultMatrix = matrix;
  };

  setScatterCount = (type) => {
    switch (type) {
      case 'scatter1':
        this.scatter1Count += 1;
        break;
      case 'scatter2':
        this.scatter2Count += 1;
        break;
      case 'reset':
        this.scatter1Count = 0;
        this.scatter2Count = 0;
        break;
    }
  };

  setWildIcons = (icon) => {
    if (icon) this.wildIcons.push(icon);
    else this.wildIcons = [];
  };
}

export const gameModuleStore = new GameModuleStore();
